import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { normalizeToBlob } from '../../utils/image';
import {
  getGalleryUploadSignature,
  uploadToCloudinary,
} from '../../services/gallery';
import {
  GalleryContext,
  GalleryImage,
  GalleryUploadSignature,
} from '../../types/gallery';

export interface GalleryUploadRow {
  localId: string;
  fileName: string;
  previewUrl: string;
  progress: number;
  status: 'uploading' | 'error';
  errorMessage?: string;
}

let uploadCounter = 0;
function nextLocalId() {
  uploadCounter += 1;
  return `gallery-upload-${uploadCounter}`;
}

export interface UseGalleryUploadsResult {
  rows: GalleryUploadRow[];
  upload: (files: File[]) => Promise<GalleryImage[]>;
  retry: (localId: string) => Promise<GalleryImage[]>;
  dismiss: (localId: string) => void;
}

function useGalleryUploads(
  context: GalleryContext | null,
): UseGalleryUploadsResult {
  const [rows, setRows] = useState<GalleryUploadRow[]>([]);
  const filesByLocalId = useRef(new Map<string, File>());
  const retryingIds = useRef(new Set<string>());
  const rowsRef = useRef<GalleryUploadRow[]>(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(
    () => () => {
      rowsRef.current.forEach(row => URL.revokeObjectURL(row.previewUrl));
    },
    [],
  );

  const updateRow = useCallback(
    (localId: string, changes: Partial<GalleryUploadRow>) => {
      setRows(current =>
        current.map(row =>
          row.localId === localId ? { ...row, ...changes } : row,
        ),
      );
    },
    [],
  );

  const settle = useCallback((localId: string) => {
    setRows(current => {
      const row = current.find(r => r.localId === localId);
      if (row) URL.revokeObjectURL(row.previewUrl);
      return current.filter(r => r.localId !== localId);
    });
    filesByLocalId.current.delete(localId);
  }, []);

  const uploadOne = useCallback(
    async (
      localId: string,
      file: File,
      preSigned?: GalleryUploadSignature,
    ): Promise<GalleryImage | null> => {
      if (!context) return null;
      try {
        const [signed, blob] = await Promise.all([
          preSigned ?? getGalleryUploadSignature(context),
          normalizeToBlob(file),
        ]);
        const image = await uploadToCloudinary(
          blob,
          file.name,
          signed,
          percent => updateRow(localId, { progress: percent }),
        );
        settle(localId);
        return image;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Could not upload that photo.';
        // The tile is too small to read a sentence in; it only marks which
        // photo failed.
        toast.error(errorMessage);
        updateRow(localId, { status: 'error', errorMessage });
        return null;
      }
    },
    [context, settle, updateRow],
  );

  const upload = useCallback(
    async (files: File[]): Promise<GalleryImage[]> => {
      const entries = files.map(file => ({ file, localId: nextLocalId() }));
      entries.forEach(({ localId, file }) =>
        filesByLocalId.current.set(localId, file),
      );

      setRows(current => [
        ...current,
        ...entries.map(({ localId, file }) => ({
          localId,
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
          progress: 0,
          status: 'uploading' as const,
        })),
      ]);

      if (!context) return [];

      let signed: GalleryUploadSignature;
      try {
        signed = await getGalleryUploadSignature(context);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Could not upload that photo.';
        toast.error(errorMessage);
        entries.forEach(({ localId }) =>
          updateRow(localId, { status: 'error', errorMessage }),
        );
        return [];
      }

      const results = await Promise.all(
        entries.map(({ localId, file }) => uploadOne(localId, file, signed)),
      );
      return results.filter((image): image is GalleryImage => image !== null);
    },
    [context, uploadOne, updateRow],
  );

  const retry = useCallback(
    async (localId: string): Promise<GalleryImage[]> => {
      if (retryingIds.current.has(localId)) return [];
      const file = filesByLocalId.current.get(localId);
      if (!file) return [];
      retryingIds.current.add(localId);
      updateRow(localId, { status: 'uploading', progress: 0 });
      try {
        const image = await uploadOne(localId, file);
        return image ? [image] : [];
      } finally {
        retryingIds.current.delete(localId);
      }
    },
    [updateRow, uploadOne],
  );

  return { rows, upload, retry, dismiss: settle };
}

export default useGalleryUploads;
